import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { format as formatDateFns } from 'date-fns'
import { ArrowUpRight } from 'lucide-react'
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip as ReTooltip, XAxis, YAxis } from 'recharts'
import apiClient from '@/api/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useMoney } from '@/components/admin/expenses/moneyUi'
import type { UseReportRange } from '@/hooks/useReportRange'
import { rangeToSearch } from '@/hooks/useReportRange'
import { chartAxis, chartTooltipBar } from '@/lib/chartTheme'
import { cn, formatDateRangeHuman } from '@/lib/utils'
import type { AdjustmentsDayRow, AdjustmentsOrderRow, AdjustmentsReport } from '@/types'
import { adjustmentReasonLabel } from '@/lib/discountControls'
import { ExportButton } from './ExportButton'
import { ReportsExportSlot } from './ReportsExportSlot'
import { REPORTS_ORDER_FILTER_KEYS, type ReportTab, type ReportsSearch } from './reportsRouteSearch'
import {
  adjustmentTotals,
  chartRows,
  discountBar,
  DISCOUNT_SOURCES,
  filterCounts,
  formatRate,
  formatShare,
  givenUpTotals,
  hasAnything,
  matchesFilter,
  orderDiscountParts,
  orderServiceChargeText,
  percentOf,
  reasonRows,
  SC_PARTS,
  serviceChargeBar,
  staffByHand,
  type AdjustmentTotals,
  type BarPart,
  type ChartMode,
  type OrderFilter,
} from './adjustmentsLogic'

interface Props {
  range: UseReportRange
}

/**
 * Reports → Discounts & service charge (F-FE-N212 / F-RPT-N51).
 *
 * Owners change the service charge bill by bill and give discounts by hand
 * far more than through promos (fleet survey 2026-11-02), and until now no
 * screen said how much, on which days, or by whom. Read top to bottom: the
 * period in two figures, the days, the people, then any day's bills.
 * Figures are ink; colour belongs to chart marks and their keys only.
 */
export function AdjustmentsTab({ range }: Props) {
  const money = useMoney()
  const navigate = useNavigate()
  const search = useSearch({ from: '/admin/reports' }) as ReportsSearch
  const [mode, setMode] = useState<ChartMode>('discounts')
  const [hideQuietDays, setHideQuietDays] = useState(true)

  const query = useQuery({
    queryKey: ['reports-v2', 'adjustments', range.fromISO, range.toISO],
    queryFn: () => apiClient.getAdjustmentsReport(range.fromISO, range.toISO),
    staleTime: 30_000,
  })
  const report = query.data?.data
  const days = useMemo(() => report?.days ?? [], [report])
  const totals = useMemo(() => adjustmentTotals(days), [days])
  const staff = useMemo(() => staffByHand(report?.staff ?? []), [report])
  const rate = report?.service_charge_rate ?? 0

  // The open day lives in the URL, so a drawer survives a refresh and can be shared.
  const openDay = search.day && days.some(d => d.date === search.day) ? search.day : undefined
  const setDay = (day: string | undefined) =>
    navigate({
      to: '/admin/reports',
      search: (prev): ReportsSearch => ({ ...(prev as ReportsSearch), ...rangeToSearch(range), tab: 'adjustments' as ReportTab, day }),
      replace: true,
    })

  const visibleDays = hideQuietDays ? days.filter(d => d.orders > 0) : days

  return (
    <div className="space-y-4">
      <ReportsExportSlot>
        <ExportButton report="adjustments" reportLabel="Discounts & service charge" fromISO={range.fromISO} toISO={range.toISO} />
      </ReportsExportSlot>

      {query.isLoading ? (
        <Skeleton className="h-[220px] w-full rounded-lg" />
      ) : query.isError ? (
        <Card>
          <CardContent className="p-8 text-sm text-muted-foreground">
            This report could not be loaded. Check the connection and pick the period again.
          </CardContent>
        </Card>
      ) : !hasAnything(totals) ? (
        <Card>
          <CardContent className="space-y-1 p-10 text-center">
            <p className="text-sm font-medium text-foreground">No discounts or service charge in {formatDateRangeHuman(range.from, range.to)}</p>
            <p className="text-sm text-muted-foreground">
              {totals.orders > 0
                ? `${totals.orders} bills were rung with neither. Pick a longer period to compare.`
                : 'No bills were completed in this period.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <PeriodBand totals={totals} rate={rate} report={report!} />
          <DayChart days={days} mode={mode} onMode={setMode} onOpenDay={setDay} />
          <DayTable
            days={visibleDays}
            allDays={days.length}
            hideQuietDays={hideQuietDays}
            onToggleQuiet={() => setHideQuietDays(v => !v)}
            totals={totals}
            onOpenDay={setDay}
          />
          <StaffTable rows={staff} />
        </>
      )}

      <DayDrawer
        day={openDay ? days.find(d => d.date === openDay) : undefined}
        onClose={() => setDay(undefined)}
        onOpenBill={(o, day) =>
          navigate({
            to: '/admin/reports',
            search: (prev): ReportsSearch => {
              const next = { ...(prev as ReportsSearch), ...rangeToSearch(range), tab: 'daily' as ReportTab, day, order: o.id }
              // An armed Daily Sales facet must never hide the bill we jump to.
              for (const k of REPORTS_ORDER_FILTER_KEYS) delete next[k]
              return next
            },
          })
        }
        money={money}
      />
    </div>
  )
}

// ── The period in two figures ───────────────────────────────────────────────

function PeriodBand({ totals: t, rate, report }: { totals: AdjustmentTotals; rate: number; report: AdjustmentsReport }) {
  const given = givenUpTotals(report.days)
  const why = reasonRows(report).filter(r => r.discounts > 0.005)
  const anyReason = why.some(r => r.code !== '')
  const money = useMoney()
  const byHandShare = percentOf(t.byHand, t.discounts)
  const discountShare = percentOf(t.discounts, t.subtotal)
  const scChanged = t.scCustomOrders + t.scWaivedOrders

  return (
    <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
      <div className="grid divide-y lg:grid-cols-2 lg:divide-x lg:divide-y-0">
        <section aria-labelledby="adj-sc" className="p-5">
          <h3 id="adj-sc" className="text-sm text-muted-foreground">
            Service charge
          </h3>
          <p className="mt-2 text-3xl font-bold tracking-tight text-foreground" title={money.full(t.serviceCharge)}>
            {money.whole(t.serviceCharge)}
          </p>
          <p className="mt-1 max-w-prose text-sm text-muted-foreground">
            {scChanged === 0 ? (
              <>Every bill was charged at your set rate of {formatRate(rate)}.</>
            ) : (
              <>
                Your set rate is {formatRate(rate)}. Staff changed it on{' '}
                <span className="font-medium text-foreground">
                  {scChanged} of {t.scConfiguredOrders + scChanged} bills
                </span>
                {t.scWaivedOrders > 0 && <>, waiving it on {t.scWaivedOrders}</>}.
              </>
            )}
          </p>
          {(given.givenUp > 0.005 || given.added > 0.005 || given.unknown > 0) && (
            <p className="mt-2 max-w-prose text-sm text-muted-foreground">
              {given.givenUp > 0.005 && (
                <>
                  <span className="font-medium text-foreground">{money.whole(given.givenUp)} given up</span> against your set rate.{' '}
                </>
              )}
              {given.added > 0.005 && <>{money.whole(given.added)} added above it. </>}
              {given.unknown > 0 && (
                <>
                  {given.unknown} earlier {given.unknown === 1 ? 'bill is' : 'bills are'} not counted, from before this was recorded.
                </>
              )}
            </p>
          )}
          <ShareBar parts={serviceChargeBar(t)} label="Service charge by how it was set" />
        </section>

        <section aria-labelledby="adj-disc" className="p-5">
          <h3 id="adj-disc" className="text-sm text-muted-foreground">
            Discounts
          </h3>
          <p className="mt-2 text-3xl font-bold tracking-tight text-foreground" title={money.full(t.discounts)}>
            {money.whole(t.discounts)}
          </p>
          <p className="mt-1 max-w-prose text-sm text-muted-foreground">
            {t.discounts > 0.005 ? (
              <>
                On {t.discountedOrders} of {t.orders} bills, {formatShare(discountShare)} of sales before discount.{' '}
                <span className="font-medium text-foreground">
                  {money.whole(t.byHand)} ({formatShare(byHandShare)}) was given by hand
                </span>
                .
              </>
            ) : (
              <>No discounts in this period.</>
            )}
          </p>
          <ShareBar parts={discountBar(t)} label="Discounts by where they came from" />
          {why.length > 0 && (
            <div className="mt-5 border-t pt-4">
              <h4 className="text-sm text-muted-foreground">Why, for discounts given by hand</h4>
              <ul className="mt-2 space-y-1.5">
                {why.map(r => (
                  <li key={r.code || 'none'} className="flex items-baseline justify-between gap-4 text-sm">
                    <span className={r.code ? 'text-foreground' : 'text-muted-foreground'}>{r.label}</span>
                    <span className="flex items-baseline gap-3 tabular-nums">
                      <span className="text-foreground">{money.whole(r.discounts)}</span>
                      <span className="w-10 text-right text-xs text-muted-foreground">{formatShare(r.share)}</span>
                    </span>
                  </li>
                ))}
              </ul>
              {!anyReason && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Turn on &ldquo;Ask for a reason&rdquo; in Settings to see why each discount was given.
                </p>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

/** A 100% bar split by part, 2px surface gaps between segments, keyed underneath with values. */
function ShareBar({ parts, label }: { parts: BarPart[]; label: string }) {
  const money = useMoney()
  if (parts.length === 0) return null
  return (
    <div className="mt-4">
      <div
        className="flex h-2.5 w-full gap-[2px] overflow-hidden rounded-full"
        role="img"
        aria-label={`${label}: ${parts.map(p => `${p.label} ${formatShare(p.share)}`).join(', ')}`}
      >
        {parts.map(p => (
          <div key={p.key} className="h-full first:rounded-l-full last:rounded-r-full" style={{ width: `${p.share}%`, background: p.colour }} />
        ))}
      </div>
      <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
        {parts.map(p => (
          <li key={p.key} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-[2px]" style={{ background: p.colour }} />
            {p.label}
            <span className="tabular-nums text-foreground">{money.whole(p.amount)}</span>
            <span className="tabular-nums">{formatShare(p.share)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ── By day ──────────────────────────────────────────────────────────────────

const Y_WIDTH = 56
const DISCOUNT_SERIES = DISCOUNT_SOURCES.map(s => ({ key: s.key, label: s.label, colour: s.colour }))
const SC_SERIES = (['configured', 'custom'] as const).map(k => ({ key: k, label: SC_PARTS[k].label, colour: SC_PARTS[k].colour }))

function DayChart({
  days,
  mode,
  onMode,
  onOpenDay,
}: {
  days: AdjustmentsDayRow[]
  mode: ChartMode
  onMode: (m: ChartMode) => void
  onOpenDay: (day: string) => void
}) {
  const money = useMoney()
  const rows = useMemo(() => chartRows(days), [days])
  const series = mode === 'discounts' ? DISCOUNT_SERIES : SC_SERIES
  // Only key what is on screen; colours stay fixed to the source either way.
  const shown = series.filter(s => rows.some(r => (r[s.key as keyof (typeof rows)[number]] as number) > 0.005))
  const dense = rows.length > 40
  const tick = (iso: string) => formatDateFns(new Date(`${iso}T12:00:00`), 'd MMM')

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 pb-2 sm:flex-row sm:items-start sm:justify-between sm:space-y-0">
        <div>
          <CardTitle className="text-base">By day</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">Select a day to see its bills.</p>
        </div>
        <div role="group" aria-label="Show" className="inline-flex rounded-md border bg-muted/40 p-0.5">
          {(
            [
              ['discounts', 'Discounts'],
              ['service_charge', 'Service charge'],
            ] as const
          ).map(([m, label]) => (
            <button
              key={m}
              type="button"
              aria-pressed={mode === m}
              onClick={() => onMode(m)}
              className={cn(
                'rounded-[5px] px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                mode === m ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        {shown.length === 0 ? (
          <p className="flex h-[200px] items-center justify-center text-sm text-muted-foreground">
            {mode === 'discounts' ? 'No discounts in this period.' : 'No service charge in this period.'}
          </p>
        ) : (
          <>
            <ul className="flex flex-wrap gap-x-4 gap-y-1 pb-2" style={{ paddingLeft: Y_WIDTH }}>
              {shown.map(s => (
                <li key={s.key} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span aria-hidden className="h-2.5 w-2.5 rounded-[2px]" style={{ background: s.colour }} />
                  {s.label}
                </li>
              ))}
            </ul>
            <div className="h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={rows}
                  margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                  barCategoryGap={dense ? '10%' : '24%'}
                  onClick={(e: { activeLabel?: string } | null) => e?.activeLabel && onOpenDay(e.activeLabel)}
                  className="cursor-pointer"
                >
                  <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="date" {...chartAxis} tickFormatter={tick} interval="preserveStartEnd" minTickGap={24} height={24} />
                  <YAxis {...chartAxis} width={Y_WIDTH} tickFormatter={v => money.axis(Number(v))} />
                  <ReTooltip {...chartTooltipBar} isAnimationActive={false} content={<DayTooltip mode={mode} series={shown} />} />
                  {shown.map(s => (
                    <Bar
                      key={s.key}
                      dataKey={s.key}
                      name={s.label}
                      stackId="day"
                      fill={s.colour}
                      // A card-coloured hairline between stacked segments: identity never rests on colour alone.
                      stroke="hsl(var(--card))"
                      strokeWidth={shown.length > 1 ? 1 : 0}
                      maxBarSize={22}
                      isAnimationActive={false}
                    >
                      {/* The cap belongs to whichever segment ends that day's column. */}
                      {rows.map(r => (
                        <Cell key={r.date} radius={(topOf(r, shown) === s.key ? [3, 3, 0, 0] : 0) as unknown as number} />
                      ))}
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

/** The last series with a value on that day: the one whose top is the column's top. */
function topOf(r: ReturnType<typeof chartRows>[number], series: { key: string }[]): string | undefined {
  for (let i = series.length - 1; i >= 0; i--) {
    if ((r[series[i].key as keyof typeof r] as number) > 0.005) return series[i].key
  }
  return undefined
}

function DayTooltip({
  active,
  payload,
  mode,
  series,
}: {
  active?: boolean
  payload?: { payload: ReturnType<typeof chartRows>[number] }[]
  mode: ChartMode
  series: { key: string; label: string; colour: string }[]
}) {
  const money = useMoney()
  const r = payload?.[0]?.payload
  if (!active || !r) return null
  const total = mode === 'discounts' ? r.discounts : r.service_charge
  return (
    <div className="min-w-[230px] rounded-md border bg-popover px-3 py-2.5 text-xs text-popover-foreground shadow-md">
      <p className="mb-2 font-medium">{formatDateFns(new Date(`${r.date}T12:00:00`), 'EEE d MMM')}</p>
      <dl className="space-y-1">
        {series.map(s => {
          const v = r[s.key as keyof typeof r] as number
          if (Math.abs(v) < 0.005) return null
          return (
            <div key={s.key} className="flex items-center justify-between gap-6">
              <dt className="flex items-center gap-2 text-muted-foreground">
                <span aria-hidden className="h-2 w-2 rounded-[2px]" style={{ background: s.colour }} />
                {s.label}
              </dt>
              <dd className="tabular-nums">{money.full(v)}</dd>
            </div>
          )
        })}
        <div className="flex items-center justify-between gap-6 border-t pt-1 font-medium">
          <dt>Total</dt>
          <dd className="tabular-nums">{money.full(total)}</dd>
        </div>
      </dl>
      <p className="mt-2 text-muted-foreground">
        {mode === 'discounts'
          ? `${r.discounted_orders} of ${r.orders} bills discounted`
          : r.custom_orders + r.waived_orders > 0
            ? `${r.custom_orders} custom, ${r.waived_orders} waived, of ${r.orders} bills`
            : `All ${r.orders} bills at your set rate`}
      </p>
    </div>
  )
}

// ── The days ────────────────────────────────────────────────────────────────

function DayTable({
  days,
  allDays,
  hideQuietDays,
  onToggleQuiet,
  totals: t,
  onOpenDay,
}: {
  days: AdjustmentsDayRow[]
  allDays: number
  hideQuietDays: boolean
  onToggleQuiet: () => void
  totals: AdjustmentTotals
  onOpenDay: (day: string) => void
}) {
  const money = useMoney()
  const quiet = allDays - days.length
  const programmes = (d: AdjustmentsDayRow) => d.discount_promo + d.discount_card_promo + d.discount_loyalty
  const byHand = (d: AdjustmentsDayRow) => d.discount_manual_bill + d.discount_manual_lines
  const num = 'px-4 py-2.5 text-right tabular-nums'

  return (
    <Card>
      <CardHeader className="flex flex-col gap-2 pb-2 sm:flex-row sm:items-end sm:justify-between sm:space-y-0">
        <div>
          <CardTitle className="text-base">Each day</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">Amounts in {money.symbol}. Select a day to see its bills.</p>
        </div>
        {(quiet > 0 || !hideQuietDays) && (
          <button type="button" onClick={onToggleQuiet} className="text-xs font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline">
            {hideQuietDays ? `Show ${quiet} days with no bills` : 'Hide days with no bills'}
          </button>
        )}
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th scope="col" rowSpan={2} className="border-b px-4 py-2.5 text-left align-bottom font-medium">Day</th>
                <th scope="col" rowSpan={2} className={cn(num, 'border-b align-bottom font-medium')}>Bills</th>
                <th scope="colgroup" colSpan={3} className="px-4 pt-2.5 text-center font-medium text-foreground">Service charge</th>
                <th scope="colgroup" colSpan={4} className="border-l px-4 pt-2.5 text-center font-medium text-foreground">Discounts</th>
              </tr>
              <tr className="border-b">
                <th scope="col" className={cn(num, 'font-medium')}>Collected</th>
                <th scope="col" className={cn(num, 'font-medium')} title="Bills where staff set a different rate or amount">Custom bills</th>
                <th scope="col" className={cn(num, 'font-medium')} title="Bills where the service charge was taken off">Waived bills</th>
                <th scope="col" className={cn(num, 'border-l font-medium')}>Total</th>
                <th scope="col" className={cn(num, 'font-medium')}>By hand</th>
                <th scope="col" className={cn(num, 'font-medium')} title="Promo codes, bank card promos and loyalty rewards">From offers</th>
                <th scope="col" className={cn(num, 'font-medium')} title="Discounts as a share of sales before discount">Of sales</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {days.map(d => (
                <tr
                  key={d.date}
                  tabIndex={0}
                  onClick={() => onOpenDay(d.date)}
                  onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onOpenDay(d.date))}
                  className="cursor-pointer transition-colors hover:bg-muted/40 focus-visible:bg-muted/60 focus-visible:outline-none"
                  aria-label={`Open the bills for ${formatDateFns(new Date(`${d.date}T12:00:00`), 'EEEE d MMMM')}`}
                >
                  <td className="whitespace-nowrap px-4 py-2.5 text-foreground">{formatDateFns(new Date(`${d.date}T12:00:00`), 'EEE d MMM')}</td>
                  <td className={num}>{d.orders}</td>
                  <td className={num}>{money.plain(d.service_charge)}</td>
                  <td className={cn(num, d.sc_custom_orders === 0 && 'text-muted-foreground/60')}>{d.sc_custom_orders || '—'}</td>
                  <td className={cn(num, d.sc_waived_orders === 0 && 'text-muted-foreground/60')}>{d.sc_waived_orders || '—'}</td>
                  <td className={cn(num, 'border-l font-medium')}>{money.plain(d.discounts)}</td>
                  <td className={num}>{byHand(d) > 0.005 ? money.plain(byHand(d)) : <span className="text-muted-foreground/60">—</span>}</td>
                  <td className={num}>{programmes(d) > 0.005 ? money.plain(programmes(d)) : <span className="text-muted-foreground/60">—</span>}</td>
                  <td className={cn(num, 'text-muted-foreground')}>{formatShare(percentOf(d.discounts, d.subtotal))}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 bg-muted/30 font-medium">
              <tr>
                <td className="px-4 py-2.5">Total</td>
                <td className={num}>{t.orders}</td>
                <td className={num}>{money.plain(t.serviceCharge)}</td>
                <td className={num}>{t.scCustomOrders}</td>
                <td className={num}>{t.scWaivedOrders}</td>
                <td className={cn(num, 'border-l')}>{money.plain(t.discounts)}</td>
                <td className={num}>{money.plain(t.byHand)}</td>
                <td className={num}>{money.plain(t.discounts - t.byHand)}</td>
                <td className={cn(num, 'text-muted-foreground')}>{formatShare(percentOf(t.discounts, t.subtotal))}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

// ── The people ──────────────────────────────────────────────────────────────

function StaffTable({ rows }: { rows: ReturnType<typeof staffByHand> }) {
  const money = useMoney()
  const num = 'px-4 py-2.5 text-right tabular-nums'
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Who gave them</CardTitle>
        <p className="mt-1 max-w-prose text-xs leading-relaxed text-muted-foreground">
          A discount counts for whoever rang the bill. A service-charge change counts for whoever made it, which can be a manager on
          someone else&rsquo;s bill.
        </p>
      </CardHeader>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <p className="px-5 pb-6 text-sm text-muted-foreground">Nobody discounted a bill or changed a service charge in this period.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="border-b bg-muted/40 text-xs text-muted-foreground">
                <tr>
                  <th scope="col" className="px-4 py-2.5 text-left font-medium">Staff</th>
                  <th scope="col" className={cn(num, 'font-medium')}>Bills rung</th>
                  <th scope="col" className={cn(num, 'font-medium')}>Discounted</th>
                  <th scope="col" className={cn(num, 'font-medium')}>By hand</th>
                  <th scope="col" className={cn(num, 'font-medium')}>From offers</th>
                  <th scope="col" className={cn(num, 'border-l font-medium')}>Custom charges set</th>
                  <th scope="col" className={cn(num, 'font-medium')}>Charges waived</th>
                  <th scope="col" className={cn(num, 'border-l font-medium')} title="Changes this person approved with their manager PIN">
                    Approved
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map(r => (
                  <tr key={r.user_id ?? 'none'}>
                    <td className="px-4 py-2.5 text-foreground">{r.name}</td>
                    <td className={num}>{r.orders || '—'}</td>
                    <td className={num}>{r.discounted_orders || '—'}</td>
                    <td className={cn(num, 'font-medium')}>{r.byHand > 0.005 ? money.plain(r.byHand) : '—'}</td>
                    <td className={num}>{r.discounts - r.byHand > 0.005 ? money.plain(r.discounts - r.byHand) : '—'}</td>
                    <td className={cn(num, 'border-l')}>{r.sc_custom_set || '—'}</td>
                    <td className={num}>{r.sc_waived_set || '—'}</td>
                    <td className={cn(num, 'border-l')}>{r.approvals || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ── One day's bills ─────────────────────────────────────────────────────────

const FILTERS: { id: OrderFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'discounted', label: 'Discounted' },
  { id: 'custom', label: 'Custom charge' },
  { id: 'waived', label: 'Waived' },
]

function DayDrawer({
  day,
  onClose,
  onOpenBill,
  money,
}: {
  day: AdjustmentsDayRow | undefined
  onClose: () => void
  onOpenBill: (o: AdjustmentsOrderRow, day: string) => void
  money: ReturnType<typeof useMoney>
}) {
  const [filter, setFilter] = useState<OrderFilter>('all')
  const query = useQuery({
    queryKey: ['reports-v2', 'adjustments', 'orders', day?.date],
    queryFn: () => apiClient.getAdjustmentOrders(day!.date, day!.date),
    enabled: !!day,
    staleTime: 30_000,
  })
  const orders = query.data?.data ?? []
  const counts = filterCounts(orders)
  const shown = orders.filter(o => matchesFilter(o, filter))
  const title = day ? formatDateFns(new Date(`${day.date}T12:00:00`), 'EEEE d MMMM') : ''

  return (
    <Sheet open={!!day} onOpenChange={o => !o && (setFilter('all'), onClose())}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
        <SheetHeader className="space-y-1 border-b px-6 py-5 text-left">
          <SheetTitle>{title}</SheetTitle>
          {day && (
            <SheetDescription>
              {money.whole(day.discounts)} in discounts on {day.discounted_orders} bills. Service charge {money.whole(day.service_charge)}
              {day.sc_custom_orders + day.sc_waived_orders > 0
                ? `, changed on ${day.sc_custom_orders + day.sc_waived_orders} bills.`
                : ', all at your set rate.'}
            </SheetDescription>
          )}
        </SheetHeader>

        <div role="group" aria-label="Show bills" className="flex flex-wrap gap-1.5 border-b px-6 py-3">
          {FILTERS.map(f => (
            <button
              key={f.id}
              type="button"
              aria-pressed={filter === f.id}
              disabled={f.id !== 'all' && counts[f.id] === 0}
              onClick={() => setFilter(f.id)}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40',
                filter === f.id ? 'border-foreground bg-foreground text-background' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {f.label} <span className="tabular-nums opacity-70">{counts[f.id]}</span>
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {query.isLoading ? (
            <div className="space-y-3 p-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : shown.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No bills on this day had a discount or a changed service charge.</p>
          ) : (
            <ul className="divide-y">
              {shown.map(o => (
                <li key={o.id}>
                  <button
                    type="button"
                    onClick={() => day && onOpenBill(o, day.date)}
                    className="group w-full px-6 py-4 text-left transition-colors hover:bg-muted/40 focus-visible:bg-muted/60 focus-visible:outline-none"
                  >
                    <div className="flex items-baseline justify-between gap-4">
                      <p className="font-medium tabular-nums text-foreground">
                        {o.order_number}
                        <span className="ml-2 font-normal text-muted-foreground">{formatDateFns(new Date(o.completed_at), 'h:mm a')}</span>
                      </p>
                      <p className="flex items-center gap-1 tabular-nums text-foreground">
                        {money.full(o.total)}
                        <ArrowUpRight aria-hidden className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                      </p>
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Rung by {o.cashier}
                      {o.credit_noted && ', later refunded in full'}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {orderDiscountParts(o).map(({ source, amount }) => (
                        <span key={source.key} className="inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs text-foreground">
                          <span aria-hidden className="h-2 w-2 rounded-[2px]" style={{ background: source.colour }} />
                          {source.short} <span className="tabular-nums">{money.full(amount)}</span>
                        </span>
                      ))}
                      {o.sc_kind !== 'configured' && o.sc_kind !== 'none' && (
                        <span className="inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs text-foreground">
                          {o.sc_kind === 'custom' && (
                            <span aria-hidden className="h-2 w-2 rounded-[2px]" style={{ background: SC_PARTS.custom.colour }} />
                          )}
                          {orderServiceChargeText(o, money.full)}
                          {o.sc_override_by && (o.sc_kind === 'custom' || o.sc_kind === 'waived') && o.sc_override_by !== o.cashier && (
                            <span className="text-muted-foreground">by {o.sc_override_by}</span>
                          )}
                        </span>
                      )}
                    </div>
                    {(o.discount_reason || o.sc_reason || o.approved_by) && (
                      <p className="mt-1.5 text-xs text-muted-foreground">
                        {o.discount_reason && (
                          <>
                            Discount: <span className="text-foreground">{adjustmentReasonLabel(o.discount_reason)}</span>
                            {o.discount_reason_note ? ` (${o.discount_reason_note})` : ''}.{' '}
                          </>
                        )}
                        {o.sc_reason && (
                          <>
                            Service charge: <span className="text-foreground">{adjustmentReasonLabel(o.sc_reason)}</span>
                            {o.sc_reason_note ? ` (${o.sc_reason_note})` : ''}.{' '}
                          </>
                        )}
                        {o.approved_by && (
                          <>
                            Approved by <span className="text-foreground">{o.approved_by}</span>.
                          </>
                        )}
                      </p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <p className="border-t px-6 py-3 text-xs text-muted-foreground">Select a bill to open its receipt in Daily Sales.</p>
      </SheetContent>
    </Sheet>
  )
}

